import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ProductFruits } from "react-product-fruits";
import moment from "moment-timezone";
import { API_URLS } from "@/api/urls";
import useGetData from "@/services/useGetData";
import useUserStore from "@/zustand/useUserStore";
import useBookingStore from "@/zustand/useBookingStore";
import { setSentryUser } from "@utils/sentry";
import AppLoadingScreen from "./AppLoadingScreen";
import CompleteProfileModal from "./CompleteProfileModal";
import type { PermissionsResponse } from "@/types/permissions";

interface AppDataLoaderProps {
  children: React.ReactNode;
}

interface LoadingState {
  profile: boolean;
  permissions: boolean;
  bookingSettings: boolean;
  timezone: boolean;
}

const AppDataLoader: React.FC<AppDataLoaderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { setUserData } = useUserStore();
  const { setUserBookingAllData } = useBookingStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loadingStates, setLoadingStates] = useState<LoadingState>({
    profile: true,
    permissions: true,
    bookingSettings: true,
    timezone: true,
  });

  const token = localStorage.getItem("accessToken");

  // Load user profile
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useGetData<Customer>(
    ["profile_list"],
    `${API_URLS.PROFILE.GET_PROFILE_DATA}`,
    {
      enabled: !!token,
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  // Load permissions
  const { data: permissionsData, isLoading: permissionsLoading } =
    useGetData<PermissionsResponse>(
      ["user_permissions", profileData?.id],
      `${API_URLS.MANAGE_USERS.USER_PERMISSIONS}${profileData?.id}/`,
      {
        enabled: !!profileData?.id,
        refetchOnWindowFocus: false,
        retry: 2,
      }
    );

  // Load booking settings
  const { data: bookingSettingsData, isLoading: bookingSettingsLoading } =
    useGetData<UserBookingAllData>(
      ["bookings_profile"],
      `${API_URLS.PROFILE.GET_ADMIN_BOOKING_POLICY}`,
      {
        enabled: !!profileData,
        refetchOnWindowFocus: false,
        retry: 2,
      }
    );

  // Load timezone settings
  const { data: regionalSettings, isLoading: timezoneLoading } = useGetData<
    RegionalSettings[]
  >(
    ["regional-and-date"],
    `${API_URLS.LANGUAGE_REGION.GET_LANGUAGE_SETTINGS}`,
    {
      enabled: !!profileData,
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  // Check if profile is complete (has required fields)
  const isProfileIncomplete = (profile: Customer) => {
    return !profile.first_name || !profile.last_name || !profile.phone;
  };

  // Update user data when profile loads and check for profile completeness
  useEffect(() => {
    if (profileData && !hasCheckedProfile) {
      setUserData(profileData);
      setSentryUser({
        id: profileData.id,
        email: profileData.email,
        username: profileData.first_name,
        first_name: profileData.first_name,
        last_name: profileData.last_name || "",
      });
      setLoadingStates((prev) => ({ ...prev, profile: false }));

      // Check if profile is incomplete and show modal
      if (isProfileIncomplete(profileData)) {
        setShowProfileModal(true);
      }
      setHasCheckedProfile(true);
    }
  }, [profileData, setUserData, hasCheckedProfile]);

  // Update booking settings when loaded
  useEffect(() => {
    if (bookingSettingsData) {
      setUserBookingAllData(bookingSettingsData);
      setLoadingStates((prev) => ({ ...prev, bookingSettings: false }));
    }
  }, [bookingSettingsData, setUserBookingAllData]);

  // Update loading states for permissions
  useEffect(() => {
    if (permissionsData !== undefined) {
      console.log("Permissions loaded:", permissionsData);
      console.log("Number of permissions:", permissionsData?.permissions?.length);
      console.log("Permission details:", JSON.stringify(permissionsData, null, 2));
      setLoadingStates((prev) => ({ ...prev, permissions: false }));
    }
  }, [permissionsData]);

  // Update loading states for timezone and configure moment
  useEffect(() => {
    if (regionalSettings !== undefined) {
      setLoadingStates((prev) => ({ ...prev, timezone: false }));

      // Configure moment timezone
      const timezoneFlag = Boolean(
        import.meta.env.VITE_TIMEZONE_FLAG?.toLowerCase() === "true"
      );
      if (regionalSettings?.[0]?.timezone && timezoneFlag) {
        moment.tz.setDefault(regionalSettings[0].timezone);
      }
    }
  }, [regionalSettings]);

  // Check if all critical data is loaded
  useEffect(() => {
    const allDataLoaded =
      profileData &&
      permissionsData &&
      bookingSettingsData &&
      regionalSettings &&
      !profileLoading &&
      !permissionsLoading &&
      !bookingSettingsLoading &&
      !timezoneLoading;

    if (allDataLoaded && !isInitialized) {
      // CRITICAL: Set cache data FIRST before marking as initialized
      // This ensures PrivateLayout can read permissions from cache immediately
      const appData = {
        profile: profileData,
        permissions: permissionsData,
        bookingSettings: bookingSettingsData,
        regionalSettings: regionalSettings,
      };
      queryClient.setQueryData(["app-initial-data"], appData);

      // Small delay to ensure smooth transition
      setTimeout(() => {
        setIsInitialized(true);
      }, 300);
    }
  }, [
    profileData,
    permissionsData,
    bookingSettingsData,
    regionalSettings,
    profileLoading,
    permissionsLoading,
    bookingSettingsLoading,
    timezoneLoading,
    isInitialized,
    queryClient,
  ]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (profileError) {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    return <Navigate to="/login" replace />;
  }

  if (!isInitialized || !token) {
    const loadingMessage = () => {
      if (loadingStates.profile) return "Loading your profile...";
      if (loadingStates.permissions) return "Setting up permissions...";
      if (loadingStates.bookingSettings) return "Loading booking settings...";
      if (loadingStates.timezone) return "Configuring timezone...";
      return "Finalizing setup...";
    };

    return <AppLoadingScreen subMessage={loadingMessage()} />;
  }

  // Cache is already set in useEffect before initialization
  // This ensures permissions are available immediately when children render

  const userInfo = {
    username: profileData?.email || "",
    email: profileData?.email || "",
    firstname: profileData?.first_name || "",
    lastname: profileData?.last_name || "",
  };

  const handleProfileComplete = () => {
    // Refetch profile data after successful update
    queryClient.invalidateQueries({ queryKey: ["profile_list"] });
    setShowProfileModal(false);
  };

  const handleSignOut = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.replace("/login");
  };

  // Only load ProductFruits on allowed domains to avoid CORS issues
  const shouldLoadProductFruits = () => {
    const currentDomain = window.location.hostname;
    const allowedDomains = [
      'localhost',
      'teelogix.com',
      'demo-admin.teelogix.com',
      // Add your production domains here
    ];

    return allowedDomains.some(domain =>
      currentDomain === domain || currentDomain.endsWith(`.${domain}`)
    );
  };

  return (
    <>
      {profileData &&
       import.meta.env.VITE_PRODUCT_FRUITS_WORKSPACE_CODE &&
       shouldLoadProductFruits() && (
        <ProductFruits
          user={userInfo}
          workspaceCode={import.meta.env.VITE_PRODUCT_FRUITS_WORKSPACE_CODE}
          language={import.meta.env.VITE_PRODUCT_FRUITS_LANGUAGE}
        />
      )}
      {profileData && (
        <CompleteProfileModal
          isOpen={showProfileModal}
          currentProfile={{
            first_name: profileData.first_name || "",
            last_name: profileData.last_name || "",
            phone: profileData.phone || "",
            email: profileData.email,
          }}
          onSuccess={handleProfileComplete}
          onSignOut={handleSignOut}
        />
      )}
      {children}
    </>
  );
};

export default AppDataLoader;
